import React, { useState, useMemo } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import DateRangePickerField from './components/DateRangePickerField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/el';
import { useQuery } from '@tanstack/react-query';
import { useEmployees } from './hooks/useEmployees';
import { useShifts } from './hooks/useShifts';
import { positionsApi } from './lib/api';
import OnboardingChecklist from './components/OnboardingChecklist';
import { getShiftPosition } from './lib/shiftUtils';

dayjs.locale('el');

// Modern gradient color palette
const COLORS = [
  '#06b6d4', // cyan
  '#10b981', // emerald  
  '#84cc16', // lime
  '#eab308', // yellow
  '#f97316', // orange
  '#f472b6', // pink
  '#a78bfa', // violet
  '#60a5fa', // blue
];

// Gradient definitions for charts
const GradientDefs = () => (
  <defs>
    <linearGradient id="hoursGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#6366f1" stopOpacity={0.9} />
    </linearGradient>
    <linearGradient id="salaryGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
      <stop offset="100%" stopColor="#059669" stopOpacity={0.9} />
    </linearGradient>
  </defs>
);

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State for filters
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);
  // Default to current month (November 2025)
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  
  // React Query hooks - Much cleaner!
  // Fetch all employees (both active and inactive) for better caching
  const { data: allEmployees = [], isLoading: employeesLoading, error: employeesError } = useEmployees(undefined);
  // Filter to active employees for display
  const employees = useMemo(() => allEmployees.filter(emp => emp.active === 1), [allEmployees]);
  const { data: positions = [], isLoading: positionsLoading, error: positionsError } = useQuery({
    queryKey: ['positions'],
    queryFn: positionsApi.getAll,
    staleTime: 10 * 60 * 1000, // 10 minutes - positions rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
  // Get shifts with date filters - memoize to prevent unnecessary refetches
  const shiftFilters = useMemo(() => ({
    start_date: startDate.format('YYYY-MM-DD'),
    end_date: endDate.format('YYYY-MM-DD'),
  }), [startDate, endDate]);
  const { data: shifts = [], isLoading: shiftsLoading, error: shiftsError } = useShifts(shiftFilters);

  // Get all shifts for all-time calculations (without date filters)
  const { data: allShifts = [], isLoading: allShiftsLoading } = useShifts();

  // Calculate stats from shifts (memoized for performance)
  const stats = useMemo(() => {
    let filteredShifts = shifts;

    // Apply employee filter
    if (selectedEmployee !== 'all') {
      filteredShifts = filteredShifts.filter(shift => shift.employee_id === parseInt(selectedEmployee));
    }

    // Apply position filter (uses position snapshot on shift when available)
    if (selectedPosition !== 'all') {
      filteredShifts = filteredShifts.filter((shift) => {
        const employee = employees.find((emp) => emp.id === shift.employee_id);
        return getShiftPosition(shift, employee) === selectedPosition;
      });
    }

    const totalHours = filteredShifts.reduce((total, shift) => {
      const [sh, sm] = shift.start_time.split(':').map(Number);
      const [eh, em] = shift.end_time.split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end < start) end += 24 * 60;
      const minutes = end - start;
      return total + (minutes / 60);
    }, 0);

    const totalSalary = filteredShifts.reduce((total, shift) => {
      const employee = employees.find(emp => emp.id === shift.employee_id);
      const [sh, sm] = shift.start_time.split(':').map(Number);
      const [eh, em] = shift.end_time.split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end < start) end += 24 * 60;
      const minutes = end - start;
      const hours = minutes / 60;
      
      // Use stored hourly_wage from shift for historical accuracy
      let wageRate = shift.hourly_wage;
      if (wageRate === null || wageRate === undefined) {
        // Fallback for old shifts without stored wage
        const shiftDay = parseInt(shift.day);
        if (shiftDay === 6) {
          wageRate = employee?.saturday_wage || employee?.hourly_wage || 0;
        } else if (shiftDay === 0) {
          wageRate = employee?.sunday_wage || employee?.hourly_wage || 0;
        } else {
          wageRate = employee?.hourly_wage || 0;
        }
      }
      
      return total + (hours * wageRate);
    }, 0);

    const uniqueEmployees = [...new Set(filteredShifts.map(shift => shift.employee_id))];
    const totalEmployees = uniqueEmployees.length;
    const avgHoursPerEmployee = totalEmployees > 0 ? totalHours / totalEmployees : 0;

    // Calculate all-time salary (from all shifts)
    const totalAllTimeSalary = allShifts.reduce((total, shift) => {
      const employee = employees.find(emp => emp.id === shift.employee_id);
      const [sh, sm] = shift.start_time.split(':').map(Number);
      const [eh, em] = shift.end_time.split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end < start) end += 24 * 60;
      const minutes = end - start;
      const hours = minutes / 60;

      // Use stored hourly_wage from shift for historical accuracy
      let wageRate = shift.hourly_wage;
      if (wageRate === null || wageRate === undefined) {
        // Fallback for old shifts without stored wage
        const shiftDay = parseInt(shift.day);
        if (shiftDay === 6) {
          wageRate = employee?.saturday_wage || employee?.hourly_wage || 0;
        } else if (shiftDay === 0) {
          wageRate = employee?.sunday_wage || employee?.hourly_wage || 0;
        } else {
          wageRate = employee?.hourly_wage || 0;
        }
      }

      return total + (hours * wageRate);
    }, 0);

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      totalSalary: Math.round(totalSalary * 100) / 100,
      totalAllTimeSalary: Math.round(totalAllTimeSalary * 100) / 100,
      totalEmployees,
      avgHoursPerEmployee: Math.round(avgHoursPerEmployee * 100) / 100
    };
  }, [shifts, allShifts, employees, selectedEmployee, selectedPosition]);

  // Prepare chart data (memoized)
  const chartData = useMemo(() => {
    // Hours by employee
    const employeeHours = {};
    const employeeSalaries = {};

    shifts.forEach(shift => {
      const employee = employees.find(emp => emp.id === shift.employee_id);
      if (!employee) return;

      const [sh, sm] = shift.start_time.split(':').map(Number);
      const [eh, em] = shift.end_time.split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end < start) end += 24 * 60;
      const minutes = end - start;
      const hours = minutes / 60;
      const salary = hours * (shift.hourly_wage || employee.hourly_wage || 0);

      employeeHours[employee.name] = (employeeHours[employee.name] || 0) + hours;
      employeeSalaries[employee.name] = (employeeSalaries[employee.name] || 0) + salary;
    });

    // Prepare hours chart data
    const hoursChartData = Object.entries(employeeHours).map(([name, hours]) => ({
      name: isMobile ? name.split(' ')[0] : name, // Short name on mobile
      fullName: name,
      hours: Math.round(hours * 100) / 100
    }));

    // Prepare salary chart data
    const salaryChartData = Object.entries(employeeSalaries).map(([name, salary]) => ({
      name: isMobile ? name.split(' ')[0] : name,
      fullName: name,
      salary: Math.round(salary * 100) / 100
    }));

    // Prepare distribution data (by position)
    const positionHours = {};
    shifts.forEach(shift => {
      const employee = employees.find(emp => emp.id === shift.employee_id);
      if (!employee) return;

      const [sh, sm] = shift.start_time.split(':').map(Number);
      const [eh, em] = shift.end_time.split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end < start) end += 24 * 60;
      const minutes = end - start;
      const hours = minutes / 60;

      positionHours[getShiftPosition(shift, employee)] = (positionHours[getShiftPosition(shift, employee)] || 0) + hours;
    });

    const distributionChartData = Object.entries(positionHours).map(([position, hours]) => ({
      name: position,
      value: Math.round(hours * 100) / 100
    }));

    return {
      hoursData: hoursChartData,
      salaryData: salaryChartData,
      distributionData: distributionChartData
    };
  }, [shifts, employees, isMobile]);

  // Loading state
  // Show loading ONLY if we have no data at all (true initial load)
  const hasNoData = employees.length === 0 && positions.length === 0 && shifts.length === 0 && allShifts.length === 0;
  const isLoading = employeesLoading || positionsLoading || shiftsLoading || allShiftsLoading;
  const hasError = employeesError || positionsError || shiftsError;

  // Only show loading spinner if we have no data AND we're loading
  if (hasNoData && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Φόρτωση δεδομένων...</Typography>
        </Box>
      </Box>
    );
  }
  
  // If we have data, show it even if background refetch is happening
  // This prevents the "freeze" when navigating back to the page

  if (hasError) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Σφάλμα φόρτωσης δεδομένων
        </Typography>
        {employeesError && <Typography color="error">Employees: {employeesError.message}</Typography>}
        {positionsError && <Typography color="error">Positions: {positionsError.message}</Typography>}
        {shiftsError && <Typography color="error">Shifts: {shiftsError.message}</Typography>}
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
      <Box sx={{ maxWidth: '1600px', width: '100%' }}>
        {/* Onboarding Checklist */}
        <OnboardingChecklist />

        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          sx={{ 
            mb: { xs: 2, md: 4 }, 
            fontWeight: 600 
          }}
        >
          Στατιστικά και Αναφορές
        </Typography>

      {/* Filters - Collapsible on mobile */}
      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: isMobile ? 'pointer' : 'default',
            }}
            onClick={() => isMobile && setFiltersExpanded(!filtersExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Φίλτρα
              </Typography>
            </Box>
            {isMobile && (
              <IconButton size="small">
                {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          
          <Collapse in={!isMobile || filtersExpanded}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <DateRangePickerField
                  startDate={startDate?.toDate?.() ?? startDate}
                  endDate={endDate?.toDate?.() ?? endDate}
                  onRangeChange={(start, end) => {
                    setStartDate(dayjs(start));
                    setEndDate(dayjs(end));
                  }}
                  isMobile={isMobile}
                  label="Περίοδος"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Υπάλληλος</InputLabel>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    label="Υπάλληλος"
                  >
                    <MenuItem value="all">Όλοι οι Υπάλληλοι</MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Θέση</InputLabel>
                  <Select
                    value={selectedPosition}
                    onChange={(e) => setSelectedPosition(e.target.value)}
                    label="Θέση"
                  >
                    <MenuItem value="all">Όλες οι Θέσεις</MenuItem>
                    {positions.map((position) => (
                      <MenuItem key={position.id} value={position.name}>
                        {position.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(139, 92, 246, 0.4)'
            }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  color: 'rgba(255,255,255,0.8)'
                }}
              >
                Συνολικές Ώρες
              </Typography>
              <Typography 
                variant={isMobile ? 'h5' : 'h3'} 
                sx={{ fontWeight: 700, color: '#fff' }}
              >
                {stats.totalHours}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)'
            }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  color: 'rgba(255,255,255,0.8)'
                }}
              >
                Αμοιβές Διαστήματος
              </Typography>
              <Typography 
                variant={isMobile ? 'h5' : 'h3'} 
                sx={{ fontWeight: 700, color: '#fff' }}
              >
                €{stats.totalSalary}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            boxShadow: '0 4px 20px rgba(6, 182, 212, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(6, 182, 212, 0.4)'
            }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  color: 'rgba(255,255,255,0.8)'
                }}
              >
                Ενεργοί Υπάλληλοι
              </Typography>
              <Typography 
                variant={isMobile ? 'h5' : 'h3'} 
                sx={{ fontWeight: 700, color: '#fff' }}
              >
                {stats.totalEmployees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            boxShadow: '0 4px 20px rgba(249, 115, 22, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(249, 115, 22, 0.4)'
            }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  color: 'rgba(255,255,255,0.8)'
                }}
              >
                Μ.Ο. Ώρες/Υπάλ.
              </Typography>
              <Typography 
                variant={isMobile ? 'h5' : 'h3'} 
                sx={{ fontWeight: 700, color: '#fff' }}
              >
                {stats.avgHoursPerEmployee}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        {/* Hours by Employee Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography 
                variant={isMobile ? 'subtitle1' : 'h6'} 
                sx={{ mb: 2, fontWeight: 600, color: '#334155' }}
              >
                Ώρες ανά Υπάλληλο
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart 
                  data={chartData.hoursData}
                  margin={{ 
                    top: 10, 
                    right: isMobile ? 10 : 30, 
                    left: isMobile ? -15 : 0, 
                    bottom: isMobile ? 60 : 20 
                  }}
                >
                  <GradientDefs />
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    angle={isMobile ? -45 : -20}
                    textAnchor="end"
                    height={isMobile ? 80 : 50}
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => [value, 'Ώρες']}
                    labelFormatter={(label) => chartData.hoursData.find(d => d.name === label)?.fullName || label}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  {!isMobile && <Legend />}
                  <Bar dataKey="hours" fill="url(#hoursGradient)" name="Ώρες" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Salary by Employee Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography 
                variant={isMobile ? 'subtitle1' : 'h6'} 
                sx={{ mb: 2, fontWeight: 600, color: '#334155' }}
              >
                Αμοιβές ανά Υπάλληλο
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart 
                  data={chartData.salaryData}
                  margin={{ 
                    top: 10, 
                    right: isMobile ? 10 : 30, 
                    left: isMobile ? -15 : 0, 
                    bottom: isMobile ? 60 : 20 
                  }}
                >
                  <GradientDefs />
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    angle={isMobile ? -45 : -20}
                    textAnchor="end"
                    height={isMobile ? 80 : 50}
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => `€${value}`}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => [`€${value}`, 'Αμοιβή']}
                    labelFormatter={(label) => chartData.salaryData.find(d => d.name === label)?.fullName || label}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  {!isMobile && <Legend />}
                  <Bar dataKey="salary" fill="url(#salaryGradient)" name="Αμοιβή (€)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Distribution by Position */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography 
                variant={isMobile ? 'subtitle1' : 'h6'} 
                sx={{ mb: 2, fontWeight: 600, color: '#334155' }}
              >
                Κατανομή Ωρών ανά Θέση
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <PieChart>
                  <Pie
                    data={chartData.distributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 30 : 50}
                    outerRadius={isMobile ? 60 : 90}
                    paddingAngle={3}
                    labelLine={!isMobile}
                    label={isMobile 
                      ? false 
                      : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    fill="#8884d8"
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {chartData.distributionData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value} ώρες`]}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend 
                    layout={isMobile ? 'horizontal' : 'vertical'}
                    align={isMobile ? 'center' : 'right'}
                    verticalAlign={isMobile ? 'bottom' : 'middle'}
                    wrapperStyle={isMobile ? { fontSize: '12px' } : {}}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Table */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography 
                variant={isMobile ? 'subtitle1' : 'h6'} 
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Σύνοψη Δεδομένων
              </Typography>
              <Box sx={{ 
                mt: 2,
                maxHeight: isMobile ? 200 : 260,
                overflowY: 'auto'
              }}>
                {chartData.hoursData.map((data, index) => (
                  <Box 
                    key={data.fullName || data.name} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1,
                      pb: 1,
                      borderBottom: '1px solid #f0f0f0'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mr: 1,
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {data.fullName || data.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip 
                        label={`${data.hours}h`} 
                        size="small" 
                        sx={{ 
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                          height: { xs: 20, md: 24 }
                        }}
                      />
                      <Chip 
                        label={`€${chartData.salaryData.find(s => (s.fullName || s.name) === (data.fullName || data.name))?.salary || 0}`} 
                        size="small" 
                        color="secondary"
                        sx={{ 
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                          height: { xs: 20, md: 24 }
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
    </LocalizationProvider>
  );
}

export default Dashboard;
